<template>
  <vx-card class="mt-base mb-5" :title="activityTitle">
    <p class="mb-5">
      Within each Police Force there are a number of specialist areas you can potentially move into.
      You can normally apply to do this (if you want to) after a few years in General Duties.
    </p>
    <p class="mb-5">
      In this task, you need to look at the information below. Then, at the bottom of the page, you
      will place the different areas of policing into priority order based on your interest in those
      areas.
    </p>
    <p class="mb-5">
      <strong><u>Please Note:</u></strong> The examples given in this activity relate to NSW Police
      and other states may vary slightly. However, it will still give you an excellent idea of the
      different types of police work available.
    </p>

    <accordion-panel v-for="(role, id) in specialist_roles" :key="id" :aria-title="role.title">
      <template v-slot:title>
        <div>
          {{ role.title }}
        </div>
      </template>
      <template v-slot:content>
        <img :alt="role.alt" :src="role.image" class="img-responsive" />
      </template>
    </accordion-panel>

    <!-- <centered-image-wrapper
      classes="cursor-pointer"
      :image="videoTrigger1"
      @clicked="showIframePopup(popupVideo)"
    />
    <img :src="role.image" class="img-responsive"  alt="" />
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col my-5 flex justify-center">
        <custom-video
          aria-label="Watch specialist policing areas video"
          elem-id="video-btn-1"
          :video-image="videoTrigger1"
          :video-url="popupVideo"
        />
      </div>
    </div> -->

    <p class="mb-5">
      Drag and drop the cards for the different areas of policing into the order you would like to
      do them. Put your highest priority(s) at the top and your lowest priority(s) at the bottom of
      the list. You can also comment on why you have chosen these areas of interest.
    </p>
    <draggable
      :list="roles"
      :disabled="!isEditable"
      class="list-group mb-5"
      ghost-class="ghost"
      @start="dragging = true"
      @end="dragging = false"
    >
      <div class="list-group-item" v-for="(element, roleIndex) in roles" :key="element.name">
        <reorder-controls
          :list="roles"
          :index="roleIndex"
          :label="element.name"
          :editable="isEditable"
        />
        {{ element.name }}
      </div>
    </draggable>

    <div class="focusable-parent">
      <label>
        Comments:
        <textarea-block
          v-model="data.question2"
          :editable="isEditable"
          class="focus-visible-style"
        />
      </label>
    </div>
    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import draggable from 'vuedraggable';
import ReorderControls from '@/components/UI/ReorderControls';
import AccordionPanel from '@/components/AccordionPanel';

import CustomVideo from '@/components/CustomVideo.vue';

export default {
  components: { CustomVideo, draggable, AccordionPanel, ReorderControls },
  data() {
    return {
      videoTrigger1: require('@/assets/images/experiences/policing/policing_specialisations_splash.jpg'),
      videos: {
        1:
          $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
          '/content/policing/police_specialisations.mp4',
      },
      popupVideo:
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
        '/content/policing/police_specialisations.mp4',
      login_type: localStorage.getItem('login_type'),
      specialist_roles: {
        1: {
          title: 'Aviation Support Branch',
          image: require('@/assets/images/experiences/policing/roles_1.png'),
          alt: 'Aviation Support Branch. The Aviation Support Branch (Air Wing) provides airborne support to the NSW Police Force. It has 6 Polair aircraft (5 helicopters and 1 fixed wing) fitted with thermal imaging equipment, on-board computers, real-time video feeds to ground stations, search lights and rescue winches. Services include targeting serious, major and organised crime and counter terrorism matters, crime operations, search and rescue, emergencies and the investigation of aviation fatalities across New South Wales. To be employed there you must be a true all-rounder with a high level of fitness, an ability to learn and use state-of-the-art technology, a high level of enthusiasm and your skills as an experienced Police Officer. Photos: a pilot at the controls of a helicopter in flight, and a Polair police helicopter parked outside a hangar.',
        },
        2: {
          title: 'Bicycle Unit',
          image: require('@/assets/images/experiences/policing/roles_2.png'),
          alt: 'Bicycle Unit. Bicycle Police have been part of policing for many years and are a popular form of policing due to their high visibility. Much of the bicycle patrolling is intelligence based, focusing on street-level offences such as drug-related matters, property crime and assaults. To join the Bicycle Squad, officers require a set fitness standard and must complete an intensive, externally accredited training course at the NSW Police Academy, covering bicycle and equipment maintenance, rules and regulations, and strategic considerations. The NSW police fleet of mountain bikes is state of the art and designed by experienced Bicycle Squad officers specifically for policing duties. Photo: two officers riding police mountain bikes through a crowd.',
        },
        3: {
          title: 'Dog Unit',
          image: require('@/assets/images/experiences/policing/roles_3.png'),
          alt: 'Dog Unit. The NSW Police Force Dog Unit is part of the State Protection Group and Counter Terrorism and Special Tactics Command. It is the largest Dog Unit of any police force in Australia and is available 24 hours a day, seven days a week, with teams based around NSW as far north as Tweed Heads, south to Wagga Wagga and west to Dubbo. It deploys teams of English Springer Spaniels, Labradors and German Shepherds trained in drug detection; general purpose or general patrol (including tactical police dogs); firearm and explosive detection; and cadaver detection. Dogs are continually trained to detect new odours to combat emerging crime trends. The Unit employs both male and female handlers, most of whom work two dogs. Recruits must have completed three years of general policing, then pass a physical barrier test, a selection course and the Novice Dog Handling Course. Prior dog handling experience is not necessary. Photos: a German Shepherd police dog held on a lead, and an officer walking a police dog through bushland.',
        },
        4: {
          title: 'Detectives',
          image: require('@/assets/images/experiences/policing/roles_4.png'),
          alt: 'Detectives. Criminal Investigators, or Detectives as they are more commonly known, are generally based at Police Area Commands (PACs) throughout NSW. Detectives stationed at PACs are responsible for a broad range of investigations and require excellent all-round policing skills to investigate the different types of crime. Detectives also operate out of the State Crime Command (SCC), based at Police Headquarters, Parramatta. Detectives are highly skilled, highly trained investigators who complete an accredited university qualification as part of their employment.',
        },
        5: {
          title: 'Forensic Evidence & Technical Services Command (FETSC)',
          image: require('@/assets/images/experiences/policing/roles_5.png'),
          alt: 'Forensic Evidence and Technical Services Command (FETSC). The FETSC provides a specialised forensic response to crime and incident scenes, incorporating the collection and examination of exhibits and the scientific analysis and interpretation of evidence in accordance with investigative and judicial requirements. Career options include crime scene examination, fingerprint identification, document examination, ballistics investigation, forensic imaging and engineering investigation. Officers must complete three years of general duties policing, then undertake psychometric and aptitude tests before being assessed against the selection requirements for each discipline. Officers are also required to complete relevant tertiary qualifications and achieve expertise status in their chosen forensic field. Photos: two officers examining a crime scene in bushland with numbered evidence markers, and a forensic officer examining a surface under ultraviolet light.',
        },
        6: {
          title: 'Highway Patrol',
          image: require('@/assets/images/experiences/policing/roles_6.png'),
          alt: "Highway Patrol. The Highway Patrol's (HWP) goal is to minimise road trauma, promote orderly and safe road use and ensure the free flow of traffic. This is primarily achieved through targeted, high-profile traffic enforcement to deter and detect inappropriate road user behaviours. They use modern equipment including radar and lidar speed detection devices, automatic number plate recognition, in-car video, mobile data terminals, portable breath and drug screening devices and radio communications gear. To join the HWP team, an applicant must undertake tests and on-the-job training before securing a position. Photo: a marked NSW Police highway patrol car on a road.",
        },
        7: {
          title: 'Marine Area Command',
          image: require('@/assets/images/experiences/policing/roles_7.png'),
          alt: 'Marine Area Command. The Marine Area Command mission is to pursue excellence in maritime law enforcement and the coordination and control of marine search and rescue. It is responsible for preventing and reducing marine crime, actively patrolling NSW inland and coastal waterways, gathering intelligence and conducting operations relating to criminal activities such as drug smuggling or importation. To become a member, a police officer must have three years of policing experience and display four attributes: contemporary policing experience, a propensity to work in the marine environment and a high level of fitness; dedication to training and gaining marine qualifications; the ability and keenness to detect and arrest offenders, with a demonstrated ability to be pro-active and take action; and a desire to perform policing functions in state-of-the-art vessels on NSW waters. Photo: two officers on police jet skis on Sydney Harbour in front of the Opera House.',
        },
        8: {
          title: 'Mounted Police Unit',
          image: require('@/assets/images/experiences/policing/roles_8.png'),
          alt: "Mounted Police Unit. NSW Mounted Police is the oldest continuous Mounted Police unit in the world, formed in 1825. The unit supports public order management events by undertaking crowd control and traffic management. The majority of its work supports Local Area Commands with high-visibility patrols and local operations targeting crime. Mounted Police are used for their searching capability to cover rough terrain and have a greater ground scope than foot police. The horses are also involved in parades and ceremonies. To become a 'Mountie', a physical, medical and riding assessment test is required. Photo: a line of mounted police officers in helmets riding horses along a city street.",
        },
        9: {
          title: 'Police Transport Command',
          image: require('@/assets/images/experiences/policing/roles_9.png'),
          alt: "Police Transport Command. In May 2012, the NSW Police Force assumed full responsibility for the State's transport security. At capacity, the Police Transport Command will be made up of more than 600 sworn officers bringing major improvements to security across the transport network. The Command reduces criminal and anti-social behaviour across the transport network by responding to the most up-to-date intelligence and targeting known trouble spots through covert, highly visible and drug detection dog operations. A member of the NSW Police Force can transfer to the Police Transport Command after 2 years of service. Photo: a police officer patrolling inside a crowded bus.",
        },
        10: {
          title: 'Public Order Riot Squad',
          image: require('@/assets/images/experiences/policing/roles_10.png'),
          alt: 'Public Order Riot Squad. The Public Order and Riot Squad (PORS) began operations in January 2006 to ensure the NSW Police Force is ready and equipped to resolve any public order incident or situation. PORS police are trained and ready to deal with: riots; protests; demonstrations and events with large crowds; high visibility policing (HVP) and large operations; searches for persons, property, cannabis plantations and around internationally protected persons; canvassing of crime scenes; staffing of correctional institutions during industrial disputes; disaster victim identification (DVI); and first response to chemical, biological or radiological incidents (CBR). To join, you must meet strict medical, physical and psychological screening and be a currently accredited Operations Support Group (OSG) operative. Photo: riot squad officers in helmets, face shields and protective gear holding batons and riot shields.',
        },
        11: {
          title: 'Rescue and Bomb Disposal Unit',
          image: require('@/assets/images/experiences/policing/roles_11.png'),
          alt: 'Rescue and Bomb Disposal Unit. The Unit is based in Sydney, with the support of country-based rescue units in Newcastle, Wollongong, Blue Mountains, Bathurst, Goulburn and Lismore, and provides an around-the-clock response across NSW. Its capability covers general land and vertical rescue, bomb appraisal and disposal, search and rescue coordination, and specialist support services such as access to dangerous and difficult-to-reach places and support to police negotiators by restraining suicidal persons. Three years of general duties experience is required before an officer is eligible to apply for the Police Rescue Operations Course to become a member of the Unit. Photo: a rescue officer in a helmet and harness on ropes beside a rocky coastal cliff.',
        },
        12: {
          title: 'Tactical Operations Unit',
          image: require('@/assets/images/experiences/policing/roles_12.png'),
          alt: "Tactical Operations Unit. In 1991 the Tactical Operations Unit (TOU) and the Negotiations Unit emerged as part of the State Protection Group (SPG). Though based in Sydney, the SPG maintains an around-the-clock capability across NSW with the support of the part-time, regionally based State Protection Support Unit (SPSU) and Police Negotiators. The TOU and Negotiations Unit work together, with the primary objective being the safe resolution of high-risk situations without loss of life, injury to persons or damage to property. They seek the most suitable and best possible officers: an officer needs several years' experience in general policing before applying, then psychometric testing (and for the TOU, a series of medical and fitness assessments) before being assessed against the selection parameters for each unit.",
        },
      },
      dragging: false,

      roles: [
        { name: 'None. Remain in General duties', id: 0 },
        { name: 'Aviation Support Branch', id: 1 },
        { name: 'Bicycle Unit', id: 2 },
        { name: 'Dog Unit', id: 3 },
        { name: 'Detectives', id: 4 },
        { name: 'Forensic Evidence & Technical Services Command (FETSC)', id: 5 },
        { name: 'Highway Patrol', id: 6 },
        { name: 'Marine Area Command', id: 7 },
        { name: 'Mounted Police Unit', id: 8 },
        { name: 'Police Transport Command', id: 9 },
        { name: 'Public Order Riot Squad', id: 10 },
        { name: 'Rescue and Bomb Disposal Unit', id: 11 },
        { name: 'Tactical Operations Unit', id: 12 },
      ],
    };
  },
  methods: {
    save() {
      let indexes = '';
      this.roles.forEach((role) => {
        if (indexes != '') indexes += ',';
        indexes += role.id;
      });
      this.data.question1 = indexes;
      this.saveActivity(this.$route.params.slug, this.data, -1);
    },
  },
  computed: computed,
  inject: inject,
  mounted() {
    if (this.data.question1) {
      let elems = this.data.question1.split(',');

      let roles_new = [];
      elems.forEach((id) => {
        let elem = this.roles.find((e) => e.id == id);
        if (elem) {
          roles_new.push(elem);
        }
      });
      this.roles = roles_new;
    }
  },
};
</script>
<style scoped>
.buttons {
  margin-top: 35px;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.list-group-item {
  border: 1px solid #ccc;
  padding: 7px 20px;
  margin-bottom: 10px;
  box-shadow: 1px 1px 3px #dedede;
}
</style>
